import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO } from "date-fns";

import { useGetPartnerOffering } from "../../data/api";
import { buildPathWithParams, useQueryString } from "../../hooks";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { useTrackProductViewedEcommerceEventOnMount } from "../../offering/analytics_ecommerce_events";
import {
  SpaceOffering,
  useLocationQuery,
  usePartnerCatalogQueryMaybe,
  useSpaceOfferingsQuery,
  useSpaceQuery,
} from "../../offering/api";
import {
  useCurrentQueryString,
  useSyncQueryStringV2,
} from "../../offering/offering_helpers";
import {
  normalizeDailyAccessOrderParams,
  toQuoteInput,
  useDailyAccessOrderParamsHandlers,
  useSpaceAvailableDaysQuery,
} from "../../offering/order_params";
import { PartnerConfig } from "../../offering/partner_config";
import {
  CheckoutBoxMobile,
  DailyAccessCheckoutBoxDesktopV2,
} from "../components";
import OfferingDetailsBase from "./template_offering_details_base";
import { Dictionary } from "lodash";
import { useStandardizeImages } from "../../data/image";
import { PricingType } from "../../data";
import { CreditOption } from "../components/credit_quantity_picker";
import { useAppendCityFromURLLocationSpaceOrOfferingID } from "../hooks/use_append_city_from_url_location_id";
import { useQuoteQuery } from "@/offering/hooks/use_quote_query";
import { useHandlePressBack } from "../hooks/use_handle_press_back";
import { getPartnerOfferingName } from "@/offering/get_partner_offering_name";

interface OfferingDetailsDailyAccessParams {
  offeringID: string;
}

export interface OfferingDetailsDailyAccessQueryParams {
  spaceID?: string;
  startDate?: string;
  endDate?: string;
  addOnID?: string;
  spaceOfferingID?: string;
  fromCatalog?: string;
  promoCode?: string;
  locationID?: string;
  city?: string;
  fromCategory?: string;
}

interface OfferingDetailsDailyAccessV2Props {
  partnerConfig: PartnerConfig;
  showCheckoutPrice?: boolean;
  showSinglePass?: boolean;
}

export function OfferingDetailsDailyAccessV2(
  props: OfferingDetailsDailyAccessV2Props
) {
  const {
    showCheckoutPrice = true,
    showSinglePass = true,
    partnerConfig,
  } = props;
  const {
    firstDayOfWeek,
    includeQuantity = true,
    showPriceInfoMessage = true,
  } = partnerConfig;
  const pageParams = useParams<OfferingDetailsDailyAccessParams>();
  const queryParams = useQueryString<OfferingDetailsDailyAccessQueryParams>();
  const pageParamsOfferingID = pageParams.offeringID;
  const offeringID = pageParamsOfferingID;
  const partnerOffering = useGetPartnerOffering(offeringID);
  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    queryParams.fromCatalog
  );

  useAppendCityFromURLLocationSpaceOrOfferingID(
    partnerOffering.offeringLocations,
    partnerOffering.template
  );
  const firstPartnerOfferingLocation = partnerOffering.offeringLocations[0];
  const foundSpaceIdFromUrlLocation = partnerOffering.offeringLocations.find(
    (loc) => loc.location.id === queryParams.locationID
  )?.spaceID;
  const defaultSpaceID = firstPartnerOfferingLocation?.spaceID;
  const history = useHistory();
  const { fbt } = useI18n();
  //TODO refactor this approach daily access single pass
  let additionalDayPassAddOn = useMemo(
    () => ({
      id: "daypass",
      name: fbt("Single pass"),
      displayName: fbt("Single pass"),
      description: "",
      daysToExpire: 0,
      numberOfEntries: 1,
      productCategory: "",
      productTypeName: "",
      price: 0,
      spaceID: "",
      addOnID: "daypass",
      sortOrder: 0,
    }),
    [fbt]
  );
  const whitelistedAddOns = useMemo(() => {
    const cloned = partnerOffering.addOns.slice();
    cloned.unshift(additionalDayPassAddOn);
    return cloned;
  }, [additionalDayPassAddOn, partnerOffering.addOns]);
  const {
    orderParams,
    orderParamsError,
    handleChangeAddOnID,
    handleChangeDateRange,
    handleChangeQuantity,
    handleChangeLocation,
    handleChangeCity,
    handleChangePromoCode,
    setOrderParams,
  } = useDailyAccessOrderParamsHandlers(whitelistedAddOns, {
    ...normalizeDailyAccessOrderParams(
      {
        ...queryParams,
        promoCode: queryParams.promoCode,
        spaceID:
          partnerOffering.offeringLocations.length > 1
            ? queryParams.spaceID || foundSpaceIdFromUrlLocation
            : defaultSpaceID,
      },
      includeQuantity
    ),
  });

  useEffect(() => {
    if (queryParams.city && !orderParams.city) {
      setOrderParams((p) => ({ ...p, city: queryParams.city }));
    }
  }, [orderParams.city, queryParams.city, setOrderParams]);

  useEffect(() => {
    if (partnerCatalog?.promoCode && !queryParams.promoCode) {
      handleChangePromoCode(partnerCatalog?.promoCode);
    }
  }, [handleChangePromoCode, partnerCatalog, queryParams.promoCode]);

  const selectedSpaceID = orderParams.spaceID;

  const selectedSpaceQuery = useSpaceQuery(selectedSpaceID);
  const { data: selectedSpace, loading } = selectedSpaceQuery;
  const defaultSpaceQuery = useSpaceQuery(defaultSpaceID);
  const { data: defaultSpace } = defaultSpaceQuery;

  const { data: spaceOfferings, loading: spaceOfferingLoading } =
    useSpaceOfferingsQuery(
      selectedSpaceID,
      partnerOffering.addOns.map((addOn) => addOn.id)
    );
  const defaultSpaceDailyPrice = defaultSpace?.pricings.find(
    (p) => p.type === PricingType.Daily
  );
  const selectedSpaceDailyPrice = selectedSpace?.pricings.find(
    (p) => p.type === PricingType.Daily
  );

  additionalDayPassAddOn.price = selectedSpaceDailyPrice
    ? selectedSpaceDailyPrice.price
    : defaultSpaceDailyPrice?.price || 0;

  const multiPasses = useMemo(() => {
    const isCreditPackage = partnerOffering.addOns.find((aO) => !!aO.credits);
    if (isCreditPackage) {
      return [];
    }

    const orderDictionary = partnerOffering.addOns.reduce<
      Dictionary<number | null>
    >((dictionary, addOn) => {
      dictionary[addOn.id] = addOn.sortOrder || null;

      return dictionary;
    }, {});
    const cloned =
      spaceOfferings?.slice()?.map((spaceOffering: SpaceOffering) => {
        spaceOffering.sortOrder = orderDictionary[spaceOffering.addOnID];

        return spaceOffering;
      }) || [];

    if (showSinglePass && !spaceOfferingLoading) {
      cloned.unshift(additionalDayPassAddOn);
    }

    return cloned;
  }, [
    additionalDayPassAddOn,
    showSinglePass,
    spaceOfferings,
    partnerOffering,
    spaceOfferingLoading,
  ]);

  const credits = useMemo(() => {
    const isNotCreditPackage = partnerOffering.addOns.find(
      (aO) => aO.credits === null || aO === undefined
    );
    if (isNotCreditPackage) {
      return [];
    }

    const credits = partnerOffering.addOns.reduce<CreditOption[]>(
      (accumulator, addOn) => {
        const offering = spaceOfferings?.find(
          (sO: any) => sO.addOnID === addOn.id
        );
        const { displayName, price, id: offeringId } = offering || {};
        if (displayName && price && offeringId && addOn.credits) {
          return [
            ...accumulator,
            {
              id: addOn.id,
              offeringId,
              displayName,
              numberOfCredits: addOn.credits,
              price: Number(price),
            },
          ];
        }
        return accumulator;
      },
      []
    );

    return credits;
  }, [partnerOffering.addOns, spaceOfferings]);

  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();
  useEffect(() => {
    if (selectedSpace) setSpaceLayoutID(selectedSpace.defaultLayoutID);
  }, [selectedSpace, setSpaceLayoutID]);

  const quoteInput = useMemo(
    () =>
      selectedSpaceID && spaceLayoutID
        ? toQuoteInput(
            selectedSpaceID,
            partnerOffering.partner.id,
            orderParams,
            "WL",
            {
              layoutID: spaceLayoutID,
              attendees: 1,
            }
          )
        : null,
    [selectedSpaceID, partnerOffering.partner.id, orderParams, spaceLayoutID]
  );
  const {
    isOutsideRange,
    isBlockedDate,
    handleNextVisibleDates,
    availableDays,
    config: availabilityConfig,
  } = useSpaceAvailableDaysQuery(selectedSpaceID, {
    dateRange: orderParams.dateRange,
  });
  const selectedLocationQuery = useLocationQuery(selectedSpace?.locationID);
  const { data: location } = selectedLocationQuery;
  const {
    data: quote,
    error: quoteError,
    loading: quoteLoading,
  } = useQuoteQuery(quoteInput);

  const queryString = useCurrentQueryString(orderParams);
  useSyncQueryStringV2(orderParams);

  const handleContinue = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      `/offerings/checkout/${pageParamsOfferingID}?${queryString}`
    );
    history.push(pathWithParams);
  }, [history, pageParamsOfferingID, queryString]);

  const { handlePressBackFromDetailsPage } = useHandlePressBack();

  const handlePressBack = useCallback(() => {
    handlePressBackFromDetailsPage(
      queryParams.fromCategory
        ? `category/${queryParams.fromCategory}`
        : `catalog/${partnerOffering.partner.partnerSlug}`
    );
  }, [
    handlePressBackFromDetailsPage,
    partnerOffering.partner.partnerSlug,
    queryParams.fromCategory,
  ]);

  useEffect(() => {
    if (queryParams.spaceID && !loading && !selectedSpace) {
      const pathWithParams = buildPathWithParams(`/offerings/${offeringID}`);
      history.replace(pathWithParams);
    }
  }, [selectedSpace, queryParams.spaceID, offeringID, history, loading]);

  const offeringName =
    partnerOffering.offeringTitle && location
      ? fbt(
          fbt.param("productTypeName", partnerOffering.offeringTitle) +
            " to " +
            fbt.param("locationName", location.name)
        )
      : "";
  const selectedSpaceOffering = partnerOffering.addOns.find(
    (po) => po.addOnID === orderParams.addOnID
  );

  const images = useStandardizeImages(
    partnerOffering,
    location,
    selectedSpace,
    selectedSpaceID
  );

  useTrackProductViewedEcommerceEventOnMount(
    getPartnerOfferingName(partnerOffering),
    selectedSpace,
    location,
    quote,
    selectedSpaceOffering
  );

  const checkoutBox = (
    <DailyAccessCheckoutBoxDesktopV2
      includeQuantity={includeQuantity}
      orderParamsErrors={orderParamsError}
      firstDayOfWeek={firstDayOfWeek}
      orderParams={orderParams}
      multiPasses={multiPasses || []}
      isOutsideRange={isOutsideRange}
      isBlockedDate={isBlockedDate}
      onChangeAddOnID={handleChangeAddOnID}
      onChangeLocation={handleChangeLocation}
      onChangeDateRange={handleChangeDateRange}
      onChangeQuantity={handleChangeQuantity}
      onChangeCity={handleChangeCity}
      onContinue={handleContinue}
      onNextVisibleDates={handleNextVisibleDates}
      locations={partnerOffering.offeringLocations}
      location={location}
      quote={quote ? quote : undefined}
      space={selectedSpace}
      datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
      quoteLoading={quoteLoading}
      quoteError={quoteError}
      showCheckoutPrice={showCheckoutPrice}
      showDiscountLabel={partnerOffering.showDiscountLabel}
      showPriceInfoMessage={showPriceInfoMessage}
      availableDays={availableDays
        ?.filter((d) => d.availableFullDay)
        .map((d) => parseISO(d.date))}
      multiDayBookingAllowed={availabilityConfig.multiDayBookingAllowed}
      credits={credits}
      showQuoteDetails
      singleDaySelect={partnerOffering.singleDaySelect}
    />
  );

  return (
    <OfferingDetailsBase
      partnerOffering={partnerOffering}
      partnerOfferingID={offeringID}
      partnerConfig={partnerConfig}
      selectedSpace={selectedSpace}
      defaultSpace={defaultSpace}
      onPressBack={handlePressBack}
      quoteID={quote?.id}
      orderParams={orderParams}
      data={{
        ...images,
        offeringName:
          !selectedSpaceID && partnerOffering.offeringTitle
            ? partnerOffering.offeringTitle
            : offeringName,
        location: location,
      }}
      checkoutBoxDesktop={checkoutBox}
      checkoutBoxMobile={
        <CheckoutBoxMobile
          checkoutBox={checkoutBox}
          quote={quote}
          space={selectedSpace}
        />
      }
    />
  );
}
